'use client';

import Link from 'next/link';
import { useCallback, useState } from 'react';
import { FaInstagram } from 'react-icons/fa';
import { LuExternalLink } from 'react-icons/lu';
import { twMerge } from 'tailwind-merge';
import type { FacilityData, FacilitySection } from '@/app/_lib/parseFacilities';
import { FacilityIcon } from './FacilityIcons';

interface InteractiveLocationsProps {
  facilitySections: FacilitySection[];
  onFacilityClick: (facility: FacilityData) => void;
}

export function InteractiveLocations({ facilitySections, onFacilityClick }: InteractiveLocationsProps) {
  const [selectedFacility, setSelectedFacility] = useState<FacilityData | null>(null);

  const handleFacilityClick = useCallback(
    (facility: FacilityData) => {
      if (facility.latitude && facility.longitude) {
        setSelectedFacility(facility);
        onFacilityClick(facility);
        // Scroll to map
        document.getElementById('map-container')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
    [onFacilityClick],
  );

  // Flatten all facilities from all sections for a unified grid
  const allFacilities = facilitySections.flatMap((section) => section.facilities);

  const corporateFacility = allFacilities.find((facility) => facility.type === 'corporate');
  const adultResidentialFacilities = allFacilities.filter((facility) => facility.type === 'adult-residential');
  const elderlyResidentialFacilities = allFacilities.filter((facility) => facility.type === 'elderly-residential');

  const columnOneFacilities = [...(corporateFacility ? [corporateFacility] : []), ...adultResidentialFacilities];

  const renderFacilityCard = (facility: FacilityData) => (
    <button
      type='button'
      key={facility.name}
      className={twMerge(
        // base styles
        'rounded-lg border border-l-[6px] bg-white shadow-sm transition-all duration-200 dark:bg-gray-800 w-full text-left',

        // default neutral borders (top/right/bottom only)
        'border-t-gray-200 border-r-gray-200 border-b-gray-200',
        'dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700',

        // type-specific left color + hover all sides
        facility.type === 'corporate' && 'border-l-indigo-600 hover:border-indigo-600 dark:hover:border-indigo-500',
        facility.type === 'adult-residential' && 'border-l-teal-500 hover:border-teal-500 dark:hover:border-teal-400',
        facility.type === 'elderly-residential' &&
          'border-l-amber-400 hover:border-amber-400 dark:hover:border-amber-300',
        !['corporate', 'adult-residential', 'elderly-residential'].includes(facility.type) &&
          'border-l-gray-300 hover:border-gray-400 dark:hover:border-gray-500',

        // active/selected overrides everything to primary
        selectedFacility?.name === facility.name && 'border-primary dark:border-primary scale-105 shadow-lg',

        // hover cursor/scale effect if it has coords
        facility.latitude && facility.longitude && 'cursor-pointer hover:scale-105 hover:shadow-md',
      )}
      onClick={() => handleFacilityClick(facility)}
      disabled={!facility.latitude || !facility.longitude}
    >
      <div className='p-4'>
        <div className='flex items-start space-x-3'>
          <div className='mt-1 flex-shrink-0'>
            <FacilityIcon type={facility.type} className='h-5 w-5' />
          </div>
          <div className='flex min-w-0 flex-1 flex-col items-start justify-start'>
            <div className='flex items-center gap-3'>
              {facility.website ? (
                <Link
                  href={facility.website}
                  className='hover:text-primary dark:hover:text-primary-light inline-flex items-center gap-2 text-lg font-semibold text-black transition-colors duration-150 dark:text-white dark:text-shadow-white'
                  target='_blank'
                  rel='noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  {facility.name} <LuExternalLink className='h-4 w-4' />
                </Link>
              ) : (
                <h4 className='block text-lg font-semibold text-gray-900 dark:text-gray-100'>{facility.name}</h4>
              )}
              {facility.instagram && (
                <Link
                  href={facility.instagram}
                  className='hover:text-primary dark:hover:text-primary-light text-gray-600 transition-colors duration-150 dark:text-gray-400'
                  target='_blank'
                  rel='noreferrer'
                  onClick={(e) => e.stopPropagation()}
                  title='Follow us on Instagram'
                >
                  <FaInstagram className='h-4 w-4' />
                </Link>
              )}
            </div>
            <p className='mt-1 text-gray-600 dark:text-gray-300'>{facility.phone}</p>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              {facility.address ? <span>{facility.address}</span> : <span>&nbsp;</span>}
            </p>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              {facility.license ? <span>{facility.license}</span> : <span>&nbsp;</span>}
            </p>
            {facility.latitude && facility.longitude && (
              <p className='hover:text-primary dark:hover:text-primary-light/80 mt-2 inline text-xs text-black dark:text-white'>
                Click to view on map
              </p>
            )}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div className='grid gap-y-10 md:grid-cols-2 md:gap-x-10 md:gap-y-0'>
      <div className='space-y-6'>
        <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
          Corporate Office & Adult Residential Facilities
        </h3>
        {columnOneFacilities.map((facility) => renderFacilityCard(facility))}
      </div>
      <div className='space-y-6'>
        <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
          Residential Care Facilities for the Elderly
        </h3>
        {elderlyResidentialFacilities.map((facility) => renderFacilityCard(facility))}
      </div>
    </div>
  );
}
